import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SuccessModal from '../components/SuccessModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useEmployeeData } from '../components/EmployeeContext';

const BookRide = () => {
  const company = localStorage.getItem('name') || '';
  const key = localStorage.getItem('key') || '';
  const cid = localStorage.getItem('authToken') || '';

  const [isModalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    employee: '',
    pickup: '',
    dropoff: '',
    carType: 'Business',
    pickupTime: '',
    paymentSource: 'Cash',
    company,
    cid,
    key,
  });

  const [rideInfo, setRideInfo] = useState({
    distance: null,
    eta: null,
    fare: null,
  });

  const { employeeData } = useEmployeeData();

  // Refs for the Google Places Autocomplete
  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);

  // Refs for autocomplete instances
  const pickupAutocomplete = useRef(null);
  const dropoffAutocomplete = useRef(null);

  // Load Google Maps API script
  useEffect(() => {
    // Check if script is already loaded
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyB9ea0A-mjnD5iHfT9X8Dn5YYH4_KZopLI&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    } else {
      initAutocomplete();
    }

    return () => {
      // Clean up event listeners if component unmounts
      if (pickupAutocomplete.current) {
        window.google.maps.event.clearInstanceListeners(
          pickupAutocomplete.current
        );
      }
      if (dropoffAutocomplete.current) {
        window.google.maps.event.clearInstanceListeners(
          dropoffAutocomplete.current
        );
      }
    };
  }, []);

  // Initialize autocomplete
  const initAutocomplete = () => {
    if (
      window.google &&
      window.google.maps &&
      pickupInputRef.current &&
      dropoffInputRef.current
    ) {
      // Initialize pickup autocomplete
      pickupAutocomplete.current = new window.google.maps.places.Autocomplete(
        pickupInputRef.current
      );
      pickupAutocomplete.current.addListener('place_changed', () => {
        const place = pickupAutocomplete.current.getPlace();
        if (place && place.formatted_address) {
          setFormData((prev) => ({
            ...prev,
            pickup: place.formatted_address,
          }));

          // Calculate if both locations are set
          if (formData.dropoff) {
            calculateDistanceAndFare();
          }
        }
      });

      // Initialize dropoff autocomplete
      dropoffAutocomplete.current = new window.google.maps.places.Autocomplete(
        dropoffInputRef.current
      );
      dropoffAutocomplete.current.addListener('place_changed', () => {
        const place = dropoffAutocomplete.current.getPlace();
        if (place && place.formatted_address) {
          setFormData((prev) => ({
            ...prev,
            dropoff: place.formatted_address,
          }));

          // Calculate if both locations are set
          if (formData.pickup) {
            calculateDistanceAndFare();
          }
        }
      });
    }
  };

  // Calculate distance and fare
  const calculateDistanceAndFare = () => {
    if (!formData.pickup || !formData.dropoff) return;

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: formData.pickup,
        destination: formData.dropoff,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          const route = result.routes[0];

          // Get distance in kilometers
          const distanceInMeters = route.legs[0].distance.value;
          const distanceInKm = distanceInMeters / 1000;

          // Get duration in minutes
          const durationInSeconds = route.legs[0].duration.value;
          const durationInMinutes = Math.round(durationInSeconds / 60);

          // Calculate fare based on time of day
          const fare = calculateFare(distanceInKm, formData.pickupTime);

          setRideInfo({
            distance: distanceInKm.toFixed(2),
            eta: durationInMinutes,
            fare: fare.toFixed(2),
          });
        } else {
          // toast.error('Unable to calculate route. Please check the addresses.');
        }
      }
    );
  };

  // Calculate fare based on distance and time of day
  const calculateFare = (distanceInKm, pickupTimeStr) => {
    // Default to current time if pickup time is not set
    let pickupTime;
    if (pickupTimeStr) {
      pickupTime = new Date(pickupTimeStr.replace(' ', 'T'));
    } else {
      pickupTime = new Date();
    }

    const hour = pickupTime.getHours();
    let baseFare, ratePerKm;

    // Daytime rate (8am to 8pm)
    if (hour >= 8 && hour < 20) {
      baseFare = 4.4;
      ratePerKm = 1.32;
    }
    // Nighttime rate (8pm to 8am)
    else {
      baseFare = 5.4;
      ratePerKm = 1.81;
    }

    return baseFare + ratePerKm * distanceInKm;
  };

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));

    // If pickup time changes, recalculate fare
    if (e.target.name === 'pickupTime' && formData.pickup && formData.dropoff) {
      calculateDistanceAndFare();
    }
  };

  // Separate handlers for location inputs to prevent clearing issues
  const handleLocationInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBookRide = async () => {
    if (
      !formData.employee ||
      !formData.pickup ||
      !formData.dropoff ||
      !formData.carType ||
      !formData.pickupTime ||
      !formData.paymentSource
    ) {
      alert('Please fill in all required fields.');
      return;
    }

    const data = new URLSearchParams();
    data.append('employee', formData.employee);
    data.append('pickup', formData.pickup);
    data.append('dropoff', formData.dropoff);
    data.append('carType', formData.carType);
    data.append('pickupTime', formData.pickupTime);
    data.append('paymentSource', formData.paymentSource);
    data.append('company', formData.company);
    data.append('cid', formData.cid);
    data.append('key', formData.key);

    // Add calculated distance, duration and fare
    if (rideInfo.distance) {
      data.append('distance', rideInfo.distance);
      data.append('eta', rideInfo.duration);
    
      data.append('fare', rideInfo.fare);
    }

    try {
      const response = await fetch(
        'https://app.mindstreamsoft.com/book_ride.php',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: data,
        }
      );

      const result = await response.json();
      if (response.ok) {
        const successMessage = result?.message || 'Ride booked successfully!';
        toast.success(successMessage, {
          autoClose: 2000,
          style: { zIndex: 1051 },
        });

        setFormData({
          employee: '',
          pickup: '',
          dropoff: '',
          carType: 'Business',
          pickupTime: '',
          paymentSource: 'Cash',
          company: localStorage.getItem('name'),
          cid: localStorage.getItem('cid'),
          key: localStorage.getItem('key'),
        });

        setRideInfo({
          distance: null,
          eta: null,
          fare: null,
        });

        openModal();
      } else {
        throw new Error(result?.message || `Server Error: ${response.status}`);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(
        error.message || 'Failed to book the ride. Please try again.'
      );
    }
  };

  // Trigger calculation when both fields are filled
  useEffect(() => {
    if (formData.pickup && formData.dropoff) {
      calculateDistanceAndFare();
    }
  }, [formData.pickup, formData.dropoff]);

  return (
    <div className='container'>
      <div className='row flex-column-reverse flex-lg-row'>
        <div className='col-lg-6 col-md-12 mb-3 p-5'>
          {/* Ride Info Section */}
          {rideInfo.distance && (
            <div className='alert alert-info mb-4'>
              <div className='d-flex justify-content-between'>
                <div>
                  <strong>Distance:</strong> {rideInfo.distance} km
                </div>
                <div>
                  <strong>Duration:</strong> {rideInfo.eta} min
                </div>
                <div>
                  <strong>Fare:</strong> €{rideInfo.fare}
                </div>
              </div>
            </div>
          )}

          <div>
            <div className='mb-4 d-flex align-items-center'>
              <label
                className='me-2'
                style={{ minWidth: '130px', color: '#969696' }}
              >
                Passenger
              </label>
              <select
                className='form-select'
                name='employee'
                value={formData.employee}
                onChange={handleChange}
                style={{ flex: 1 }}
              >
                <option value=''>Select employee name</option>
                {employeeData.map((employee) => (
                  <option key={employee.id} value={employee.name}>
                    {employee.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='mb-4 d-flex align-items-center'>
              <label
                className='me-2'
                style={{ minWidth: '130px', color: '#969696' }}
              >
                Pickup
              </label>
              <input
                type='text'
                className='form-control'
                name='pickup'
                value={formData.pickup}
                onChange={handleLocationInputChange}
                placeholder='Enter pickup location'
                ref={pickupInputRef}
              />
            </div>
            <div className='mb-4 d-flex align-items-center'>
              <label
                className='me-2'
                style={{ minWidth: '130px', color: '#969696' }}
              >
                Drop Off
              </label>
              <input
                type='text'
                className='form-control'
                name='dropoff'
                value={formData.dropoff}
                onChange={handleLocationInputChange}
                placeholder='Enter dropoff location'
                ref={dropoffInputRef}
              />
            </div>
            <div className='mb-4 d-flex align-items-center'>
              <label
                className='me-2'
                style={{ minWidth: '130px', color: '#969696' }}
              >
                Car Type
              </label>
              <select
                className='form-select'
                name='carType'
                value={formData.carType}
                onChange={handleChange}
              >
                <option value='Business'>Business</option>
                <option value='Economy'>Economy</option>
                <option value='Luxury'>Luxury</option>
              </select>
            </div>
            <div className='mb-4 d-flex align-items-center'>
              <label
                className='me-2'
                style={{ minWidth: '130px', color: '#969696' }}
              >
                Pickup Date & Time
              </label>
              <input
                type='datetime-local'
                className='form-control'
                name='pickupTime'
                value={
                  formData.pickupTime
                    ? formData.pickupTime.replace(' ', 'T').slice(0, 16)
                    : ''
                }
                onChange={(e) => {
                  const formattedDateTime =
                    e.target.value.replace('T', ' ').replace(/-/g, '-') + ':00';

                  handleChange({
                    target: {
                      name: 'pickupTime',
                      value: formattedDateTime,
                    },
                  });
                }}
              />
            </div>
            <div className='mb-4 d-flex align-items-center'>
              <label
                className='me-2'
                style={{ minWidth: '130px', color: '#969696' }}
              >
                Payment
              </label>
              <select
                className='form-select'
                name='paymentSource'
                value={formData.paymentSource}
                onChange={handleChange}
              >
                <option value='Cash'>Cash</option>
                <option value='Credit Card'>Credit Card</option>
                <option value='Digital Wallet'>Digital Wallet</option>
              </select>
            </div>
            <div className='d-flex justify-content-between align-items-center'>
              <Link
                to='/dashboard'
                className='btn mt-md-3'
                style={{ color: '#f37a20', fontWeight: '600' }}
              >
                View Past Rides
              </Link>
              <button
                className='btn mt-md-3'
                onClick={handleBookRide}
                style={{
                  backgroundColor: '#f37a20',
                  color: '#fff',
                  borderRadius: '10px',
                }}
              >
                Book Ride Now
              </button>
              <SuccessModal isOpen={isModalOpen} onClose={closeModal} />
            </div>
          </div>
        </div>
        <div className='col-lg-6 col-md-12 mb-3 p-4'>
          <div
            className='map-container'
            style={{ height: '550px', width: '100%' }}
          >
            {formData.pickup && formData.dropoff ? (
              <iframe
                title='Google Map'
                src={`https://www.google.com/maps/embed/v1/directions?key=AIzaSyB9ea0A-mjnD5iHfT9X8Dn5YYH4_KZopLI&origin=${encodeURIComponent(
                  formData.pickup
                )}&destination=${encodeURIComponent(
                  formData.dropoff
                )}&mode=driving&zoom=15`}
                style={{ border: '0', height: '100%', width: '100%' }}
                allowFullScreen=''
                loading='lazy'
              ></iframe>
            ) : (
              <iframe
                title='Google Map'
                src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2381.681797268639!2d-6.260309684349727!3d53.3498051799791!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48670e9d6c92d7c3%3A0x2776a88dddc5ea5f!2sDublin!5e0!3m2!1sen!2sie!4v1679245534743!5m2!1sen!2sie'
                style={{ border: '0', height: '100%', width: '100%' }}
                allowFullScreen=''
                loading='lazy'
              ></iframe>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookRide;
